import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, EmptyState, ErrorAlert } from '../components/ui';
import * as publicApi from '../api/public';
import { ui, cn } from '../lib/ui';
import { getVisitorId } from '../lib/visitorId';
import { useToast } from '../components/toast';

/**
 * One public video: a Watch button that plays it inline as a preview
 * (docs/Decisions.md D65 item 1), or asks the visitor to join once the
 * organization's free previews are used.
 */
function VideoCard({ video, orgSlug, onWatched, onLimit, playing, setPlaying }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);

  async function watch() {
    setLoading(true);
    try {
      const data = await publicApi.watchOrgVideo(orgSlug, video.videoId, getVisitorId());
      setUrl(data.watchUrl);
      setPlaying(video.videoId);
      onWatched(data);
    } catch (err) {
      if (err.details?.previewLimitReached) onLimit(err.message);
      else toast.error(err.message || 'The video could not be played');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={cn(ui.card, 'p-4!')}>
      {url && playing === video.videoId ? (
        <video
          className="mb-3 aspect-video w-full rounded-dcon-md bg-black"
          src={url}
          controls
          autoPlay
          aria-label={video.title}
        />
      ) : (
        video.thumbnailUrl && (
          <img
            src={video.thumbnailUrl}
            alt={video.title}
            className="mb-3 aspect-video w-full rounded-dcon-md object-cover"
          />
        )
      )}
      <h3 className="mt-0 mb-2 text-title-s">{video.title}</h3>
      {video.description && (
        <p className={cn('m-0 line-clamp-2 text-body-s', ui.muted)}>{video.description}</p>
      )}
      {video.duration != null && (
        <p className={cn('mt-1 mb-0 text-body-s', ui.muted)}>
          {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
        </p>
      )}
      {!(url && playing === video.videoId) && (
        <button
          type="button"
          className={cn(ui.btnSecondary, ui.btnSm, 'mt-3')}
          onClick={watch}
          disabled={loading}
          aria-label={`Watch ${video.title}`}
        >
          {loading ? 'Loading...' : video.freeToWatch ? 'Watch (free)' : 'Watch'}
        </button>
      )}
    </div>
  );
}

export default function PublicVideos() {
  const { orgSlug } = useParams();

  const [org, setOrg] = useState(null);
  const [videos, setVideos] = useState([]);
  const [previewVideos, setPreviewVideos] = useState(0);
  const [previewsUsed, setPreviewsUsed] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [orgData, videosData] = await Promise.all([
        publicApi.getOrgProfile(orgSlug),
        publicApi.listOrgVideos(orgSlug, { limit: 50 }),
      ]);
      if (id !== runIdRef.current) return;
      setOrg(orgData);
      setVideos(videosData.videos || []);
      setPreviewVideos(videosData.previewVideos || 0);
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Failed to load videos');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={ui.publicPage}>
        <Spinner />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className={ui.publicPage}>
        <div className={ui.publicContainer}>
          <ErrorAlert message={error || 'Organization not found'} />
        </div>
      </div>
    );
  }

  return (
    <div className={ui.publicPage}>
      <header className={ui.publicHeader}>
        <div className={ui.publicContainer}>
          <div className="flex items-center gap-3">
            {org.logoUrl && (
              <Link to={`/o/${orgSlug}`} className="text-inherit no-underline hover:no-underline">
                <img
                  src={org.logoUrl}
                  alt={`${org.name} logo`}
                  className={cn(ui.publicOrgLogo, 'size-9!')}
                />
              </Link>
            )}
            <Link
              to={`/o/${orgSlug}`}
              className="font-semibold text-inherit no-underline hover:no-underline"
            >
              {org.name}
            </Link>
          </div>
        </div>
      </header>

      <main className={cn(ui.publicContainer, 'pt-8')}>
        <h1 className={ui.h1}>Videos</h1>

        {videos.length === 0 && <EmptyState message="No public videos available at this time." />}

        {videos.length > 0 && (
          <p className={cn(ui.muted, 'mb-4')}>
            {previewsUsed === null
              ? `You can watch ${previewVideos === 1 ? 'one video' : `${previewVideos} videos`} before joining ${org.name}. Members get more.`
              : `You have watched ${previewsUsed} of ${previewVideos} free ${previewVideos === 1 ? 'video' : 'videos'}.`}
          </p>
        )}

        {limitMessage && (
          <div
            className={cn(ui.alertInfo, 'mb-4 flex flex-wrap items-center justify-between gap-3')}
            role="status"
          >
            <span>{limitMessage}</span>
            <div className="flex gap-2">
              <Link to={`/o/${orgSlug}/register`} className={ui.btnPrimary}>
                Join {org.name}
              </Link>
              <Link to={`/o/${orgSlug}/login`} className={ui.btnSecondary}>
                Sign in
              </Link>
            </div>
          </div>
        )}

        {videos.length > 0 && (
          <div className={cn(ui.grid, ui.gridMd2)}>
            {videos.map(v => (
              <VideoCard
                key={v.videoId}
                video={v}
                orgSlug={orgSlug}
                playing={playing}
                setPlaying={setPlaying}
                onWatched={data => {
                  setPreviewsUsed(data.previewsUsed);
                  setLimitMessage(null);
                }}
                onLimit={setLimitMessage}
              />
            ))}
          </div>
        )}
      </main>

      <footer className={ui.publicFooter}>
        <div className={ui.publicContainer}>
          <p className={cn(ui.muted, 'm-0 text-body-s')}>&copy; {org.name}</p>
        </div>
      </footer>
    </div>
  );
}
